import { useState } from 'react';
import { Download } from 'lucide-react';
import { useAuth } from '@/auth/useAuth';

const costTypes = [
  { value: 'all', label: 'All costs' },
  { value: 'software', label: 'Software' },
  { value: 'mobile', label: 'Mobile / SIM' },
  { value: 'contracts', label: 'Contracts' },
] as const;

/** Builds the export query string. Pure — unit-tested. */
export function buildExportUrl(costType: string, period: string): string {
  const params = new URLSearchParams();
  if (costType) params.set('costType', costType);
  if (period) params.set('period', period);
  const qs = params.toString();
  return `/api/finance/export${qs ? `?${qs}` : ''}`;
}

export function FinanceExport() {
  const { canExportFinance } = useAuth();
  const [costType, setCostType] = useState('all');
  const [period, setPeriod] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!canExportFinance) {
    return (
      <section className="max-w-2xl">
        <h1 className="font-heading text-2xl font-bold text-navy">Finance export</h1>
        <p className="mt-3 rounded border border-border bg-white p-4 text-sm text-darkGrey">
          You do not have permission to export finance data. Required role: oscar-finance or oscar-admin.
        </p>
      </section>
    );
  }

  const download = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(buildExportUrl(costType, period), { credentials: 'include' });
      if (!res.ok) throw new Error(`Export failed (${res.status})`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `oscar-finance-${period || 'all'}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Export failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="max-w-2xl">
      <header className="mb-5">
        <h1 className="font-heading text-2xl font-bold text-navy">Finance export</h1>
        <p className="mt-1 text-sm text-darkGrey/80">Per-user / per-branch monthly cost CSV for journaling.</p>
      </header>

      {error && <div role="alert" className="mb-4 rounded border border-red/30 bg-red/10 px-3 py-2 text-sm text-red">{error}</div>}

      <div className="rounded border border-border bg-white p-5 shadow-sm">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <label className="block text-xs font-semibold text-darkGrey">
            Cost type
            <select value={costType} onChange={(e) => setCostType(e.target.value)} className="mt-1 w-full rounded border border-border bg-white px-2 py-1.5 text-sm font-normal">
              {costTypes.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </label>
          <label className="block text-xs font-semibold text-darkGrey">
            Period (YYYY-MM)
            <input value={period} onChange={(e) => setPeriod(e.target.value)} placeholder="2026-06" className="mt-1 w-full rounded border border-border bg-white px-2 py-1.5 text-sm font-normal" />
          </label>
        </div>
        <button type="button" onClick={download} disabled={busy} className="mt-4 inline-flex items-center gap-2 rounded bg-orange px-3 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50">
          <Download className="h-4 w-4" aria-hidden /> {busy ? 'Preparing…' : 'Download CSV'}
        </button>
      </div>
    </section>
  );
}
